import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { MultiImageUpload } from '@/components/common/MultiImageUpload'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { X } from 'lucide-react'

interface ProductFormData {
  name: string
  description: string
  short_description: string
  price: string
  compare_at_price: string
  cost_price: string
  sku: string
  brand: string
  stock: string
  category_id: string
  images: string[]
  tags: string[]
  is_featured: boolean
  discount: string
  discount_type: string
  sizes: string[]
  colors: string[]
  gender: string
}

interface ProductFormProps {
  initialData?: Partial<ProductFormData>
  categories: Array<{ id: string; name: string; parent_id: string | null }>
  onSubmit: (data: ProductFormData) => void
  onCancel: () => void
  isLoading?: boolean
  onFormChange?: (data: ProductFormData) => void
  onSaveDraft?: (data: ProductFormData) => void
}

export function ProductForm({ initialData, categories, onSubmit, onCancel, isLoading, onFormChange, onSaveDraft }: ProductFormProps) {
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    short_description: '',
    price: '',
    compare_at_price: '',
    cost_price: '',
    sku: '',
    brand: '',
    stock: '0',
    category_id: '',
    images: [],
    tags: [],
    is_featured: false,
    discount: '',
    discount_type: 'percentage',
    sizes: [],
    colors: [],
    gender: '',
    ...initialData,
  })
  const [tagInput, setTagInput] = useState('')
  const [sizeInput, setSizeInput] = useState('')
  const [colorInput, setColorInput] = useState('')
  const [autoSaving, setAutoSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!formData.name) return // Only auto-save if name exists
    const timer = setTimeout(async () => {
      if (onSaveDraft) {
        setAutoSaving(true)
        await onSaveDraft(formData)
        setAutoSaving(false)
        setLastSaved(new Date())
      }
    }, 30000)
    return () => clearTimeout(timer)
  }, [formData, onSaveDraft])

  const handleSaveDraft = async () => {
    if (onSaveDraft) {
      setAutoSaving(true)
      await onSaveDraft(formData)
      setAutoSaving(false)
      setLastSaved(new Date())
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const updateFormData = (updates: Partial<ProductFormData>) => {
    const newData = { ...formData, ...updates }
    setFormData(newData)
    onFormChange?.(newData)
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateFormData({ tags: [...formData.tags, tagInput.trim()] })
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    updateFormData({ tags: formData.tags.filter(t => t !== tag) })
  }

  const addSize = () => {
    if (sizeInput.trim() && !formData.sizes.includes(sizeInput.trim())) {
      updateFormData({ sizes: [...formData.sizes, sizeInput.trim()] })
      setSizeInput('')
    }
  }

  const removeSize = (size: string) => {
    updateFormData({ sizes: formData.sizes.filter(s => s !== size) })
  }

  const addColor = () => {
    if (colorInput.trim() && !formData.colors.includes(colorInput.trim())) {
      updateFormData({ colors: [...formData.colors, colorInput.trim()] })
      setColorInput('')
    }
  }

  const removeColor = (color: string) => {
    updateFormData({ colors: formData.colors.filter(c => c !== color) })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic">Basic</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-3">
          <div>
            <Label>Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              placeholder="Enter product name"
              required
            />
          </div>

          <div>
            <Label>Short Description</Label>
            <Textarea
              value={formData.short_description}
              onChange={(e) => updateFormData({ short_description: e.target.value })}
              placeholder="Brief product summary (1-2 lines)"
              rows={2}
            />
          </div>

          <div>
            <Label>Full Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => updateFormData({ description: e.target.value })}
              placeholder="Detailed product description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category_id} onValueChange={(value) => updateFormData({ category_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter(cat => !cat.parent_id)
                    .map((parent) => (
                      <div key={parent.id}>
                        <SelectItem value={parent.id} className="font-semibold">
                          {parent.name}
                        </SelectItem>
                        {categories
                          .filter(sub => sub.parent_id === parent.id)
                          .map((sub) => (
                            <SelectItem key={sub.id} value={sub.id} className="pl-6">
                              → {sub.name}
                            </SelectItem>
                          ))}
                      </div>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Brand</Label>
              <Input
                value={formData.brand}
                onChange={(e) => updateFormData({ brand: e.target.value })}
                placeholder="Enter brand name"
              />
            </div>
          </div>

          <div>
            <Label>SKU</Label>
            <Input
              value={formData.sku}
              onChange={(e) => updateFormData({ sku: e.target.value })}
              placeholder="Enter SKU"
            />
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => updateFormData({ price: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <Label>Compare at Price</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.compare_at_price}
                onChange={(e) => updateFormData({ compare_at_price: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label>Cost Price</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => updateFormData({ cost_price: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label>Stock Quantity</Label>
              <Input
                type="number"
                value={formData.stock}
                onChange={(e) => updateFormData({ stock: e.target.value })}
                placeholder="0"
              />
            </div>

            <div>
              <Label>Discount</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.discount}
                onChange={(e) => updateFormData({ discount: e.target.value })}
                placeholder="0"
              />
            </div>

            <div>
              <Label>Discount Type</Label>
              <Select value={formData.discount_type} onValueChange={(value) => updateFormData({ discount_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="variants" className="space-y-3">
          <div>
            <Label>Gender</Label>
            <Select value={formData.gender} onValueChange={(value) => updateFormData({ gender: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="men">Men</SelectItem>
                <SelectItem value="women">Women</SelectItem>
                <SelectItem value="unisex">Unisex</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Sizes</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'].map((size) => (
                <label
                  key={size}
                  className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-muted transition-colors"
                  style={{
                    backgroundColor: formData.sizes.includes(size) ? 'hsl(var(--primary))' : '',
                    color: formData.sizes.includes(size) ? 'hsl(var(--primary-foreground))' : '',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.sizes.includes(size)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateFormData({ sizes: [...formData.sizes, size] })
                      } else {
                        updateFormData({ sizes: formData.sizes.filter(s => s !== size) })
                      }
                    }}
                    className="hidden"
                  />
                  <span className="text-sm font-medium">{size}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Colors</Label>
            <div className="flex gap-2 mt-2 mb-3">
              {[
                { name: 'Black', hex: '#000000' },
                { name: 'White', hex: '#FFFFFF' },
                { name: 'Red', hex: '#EF4444' },
                { name: 'Blue', hex: '#3B82F6' },
                { name: 'Green', hex: '#10B981' },
                { name: 'Yellow', hex: '#F59E0B' },
              ].map((color) => (
                <label
                  key={color.name}
                  className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-muted transition-colors"
                  style={{
                    backgroundColor: formData.colors.includes(color.name) ? 'hsl(var(--primary))' : '',
                    color: formData.colors.includes(color.name) ? 'hsl(var(--primary-foreground))' : '',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.colors.includes(color.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateFormData({ colors: [...formData.colors, color.name] })
                      } else {
                        updateFormData({ colors: formData.colors.filter(c => c !== color.name) })
                      }
                    }}
                    className="hidden"
                  />
                  <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: color.hex }} />
                  <span className="text-sm font-medium">{color.name}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={colorInput}
                onChange={(e) => setColorInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
                placeholder="Add custom color"
              />
              <Button type="button" onClick={addColor} variant="outline">Add</Button>
            </div>
            {formData.colors.filter(c => !['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow'].includes(c)).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.colors.filter(c => !['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow'].includes(c)).map((color) => (
                  <span key={color} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                    {color}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeColor(color)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          {formData.sizes.length > 0 && formData.colors.length > 0 && (
            <div className="border-t pt-3">
              <Label className="mb-2 block">Stock by Variant (Size × Color)</Label>
              <div className="text-xs text-muted-foreground mb-2">
                {formData.sizes.length} sizes × {formData.colors.length} colors = {formData.sizes.length * formData.colors.length} variants
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {formData.sizes.map((size) =>
                  formData.colors.map((color) => (
                    <div key={`${size}-${color}`} className="flex items-center gap-2 p-2 border rounded text-sm">
                      <span className="font-medium min-w-[60px]">{size} - {color}</span>
                      <Input
                        type="number"
                        placeholder="Stock"
                        className="h-8 w-20"
                        defaultValue="0"
                      />
                      <Input
                        type="text"
                        placeholder="SKU (optional)"
                        className="h-8 flex-1"
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="media" className="space-y-3">
          <div>
            <Label>Product Images</Label>
            <div className="mt-2">
              <MultiImageUpload
                value={formData.images}
                onChange={(images) => updateFormData({ images })}
              />
            </div>
          </div>

          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add tag"
              />
              <Button type="button" onClick={addTag} variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                  {tag}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              checked={formData.is_featured}
              onChange={(e) => updateFormData({ is_featured: e.target.checked })}
              className="h-4 w-4"
            />
            <Label htmlFor="featured" className="cursor-pointer">Mark as featured product</Label>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-2 pt-3 border-t">
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update Product' : 'Create Product'}
        </Button>
        {onSaveDraft && (
          <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={autoSaving}>
            {autoSaving ? 'Saving...' : 'Save Draft'}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        {lastSaved && (
          <span className="text-xs text-muted-foreground self-center ml-auto">
            Saved {lastSaved.toLocaleTimeString()}
          </span>
        )}
      </div>
    </form>
  )
}
